// Hook managing search input and filtering logic.
import { useState, useEffect, useMemo } from 'react';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { getTaskTouchTimeMs } from '@/features/tasks/utils/structure/taskHierarchy';
import { SEARCH_DEBOUNCE_DELAY_MS } from '@/config/constants';

const useGoBarSearch = () => {
  const { allTasks: tasks } = useTasksContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  // Debounce the search term to prevent excessive filtering while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, SEARCH_DEBOUNCE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const results = useMemo(() => {
    const safeTasks = tasks || [];

    // Sort tasks strictly on updatedAt (descending), fallback to createdAt
    const sortedTasks = [...safeTasks].sort((a, b) => {
      const timeA = getTaskTouchTimeMs(a);
      const timeB = getTaskTouchTimeMs(b);
      return timeB - timeA; // Descending
    });

    // Empty state: Return top 5 recent tasks
    if (!debouncedTerm.trim()) {
      return sortedTasks.slice(0, 5);
    }

    // Search state: Split by space to support combination searches (e.g. "verslag #projectX")
    const terms = debouncedTerm.toLowerCase().split(/\s+/).filter(Boolean);
    return sortedTasks.filter((task) => {
      const searchableText =
        `${task.title || ''} ${task.description || ''}`.toLowerCase();
      return terms.every((term) => searchableText.includes(term));
    });
  }, [tasks, debouncedTerm]);

  return { searchTerm, setSearchTerm, results };
};

export default useGoBarSearch;
